import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PublicNav from "@/components/PublicNav";
import Footer, { startLogin } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ArrowLeft, Sparkles, FileText, AlertTriangle, Ruler, Boxes, Layers,
  TrendingDown, MessageSquare, Wrench, BadgeCheck, PhoneCall, Users
} from "lucide-react";

const WAYS = [
  { icon: FileText, title: "Compare quotations like-for-like", desc: "Line up estimates from our 500+ verified partners on the exact same scope, so you stop guessing whose rate is actually fair — and stop paying for the difference." },
  { icon: AlertTriangle, title: "Catch inflated & missing BOQ items", desc: "NCIS™ reads your BOQ line by line and flags padded rates, double-counting and items quietly left out that resurface later as 'extras'." },
  { icon: Ruler, title: "Optimise the design early", desc: "Efficient layouts and right-sized spaces cut wasted built-up area. The cheapest square foot is the one you never build — and design changes are free on paper, expensive in concrete." },
  { icon: Boxes, title: "Right-size the structure", desc: "An independent sense-check on steel and concrete helps you avoid over-engineering. Many NCR homes carry 10–15% more steel than the design actually needs." },
  { icon: Layers, title: "Match material grade to the need", desc: "Guidance on material selection so you pay a premium only where it matters — and choose equally good standard options everywhere else." },
  { icon: TrendingDown, title: "Buy at the right rate and time", desc: "Material intelligence helps you time key purchases (cement, steel, tiles) and reduce wastage instead of buying blind at peak prices." },
  { icon: MessageSquare, title: "Negotiate with confidence", desc: "Walk into contractor discussions knowing the fair number for every major cost head. The conversation changes the moment the other side knows you know." },
  { icon: Wrench, title: "Avoid rework and delays", desc: "Spot risks in drawings, soil and specifications early. Rework and stoppages are among the biggest silent overruns on Delhi-NCR sites." },
  { icon: BadgeCheck, title: "Choose verified partners, zero commission", desc: "Work directly with vetted vendors across every category — no middleman markup, no lock-in, and reviews you can actually trust." },
  { icon: PhoneCall, title: "Get expert human advice when it counts", desc: "A short independent advisory call at the big decisions — vendor selection, structural changes, material commitments — can prevent lakhs spent in the wrong direction." },
];

const STATS = [
  { icon: Users, label: "500+ verified construction partners across every category" },
  { icon: PhoneCall, label: "Independent expert human advice on demand" },
  { icon: Ruler, label: "Design optimisation to cut wasted area" },
  { icon: Layers, label: "Material selection guidance for cost & quality" },
];

export default function GuideSave20() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const createProject = () => (user ? navigate("/new-project") : startLogin("/new-project"));

  return (
    <div className="min-h-screen bg-white" data-testid="guide-save20">
      <PublicNav />

      {/* Hero */}
      <section className="hero-wash">
        <div className="max-w-4xl mx-auto px-6 pt-14 pb-10 lg:pt-20">
          <Link to="/" data-testid="guide-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0E9AC8] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 mt-6 mb-5 shadow-sm">
            <Sparkles className="h-4 w-4 text-[#0E9AC8]" strokeWidth={2} />
            <span className="text-xs font-semibold tracking-wide text-slate-600">Cost saving guide · Delhi NCR</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-extrabold text-slate-900 leading-[1.05]">
            10 ways to save up to 20% on your NCR home
          </h1>
          <div className="mt-6 space-y-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            <p>
              Building a home in Noida, Gurugram, Delhi or Ghaziabad is one of the biggest decisions a family makes —
              and one of the easiest to overspend on. Quotations rarely match, BOQs are hard to read, material rates
              keep moving, and every vendor has a different opinion. Most cost overruns in NCR don't come from bad luck;
              they come from decisions made without clear, independent information.
            </p>
            <p>
              Nirmantra.ai sits entirely on your side. With <b className="text-slate-800">500+ verified construction
              partners</b>, independent <b className="text-slate-800">expert human advice</b>, <b className="text-slate-800">design
              optimisation</b> and <b className="text-slate-800">material selection</b> guidance, homeowners can typically
              trim up to 20% off their project cost — simply by making better-informed choices at the right moments. Here's how.
            </p>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="max-w-5xl mx-auto px-6 -mt-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.label} className="card-premium rounded-2xl p-5 flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#0E9AC8]/10 flex items-center justify-center shrink-0">
                <s.icon className="h-4 w-4 text-[#0E9AC8]" strokeWidth={1.75} />
              </div>
              <p className="text-sm text-slate-600 leading-snug">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The 10 ways */}
      <section className="max-w-5xl mx-auto px-6 py-14 lg:py-20">
        <div className="space-y-8">
          {WAYS.map((w, i) => (
            <div key={w.title} data-testid={`guide-way-${i + 1}`} className="flex gap-5">
              <div className="shrink-0">
                <div className="h-14 w-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-display font-extrabold text-lg">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="pt-1">
                <div className="flex items-center gap-2">
                  <w.icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.75} />
                  <h2 className="font-display text-xl font-bold text-slate-900">{w.title}</h2>
                </div>
                <p className="mt-2 text-slate-600 leading-relaxed max-w-3xl">{w.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-12 text-xs text-slate-400" data-testid="guide-disclaimer">
          Savings potential is indicative and varies by project, location, scope and decisions taken. Nirmantra.ai provides
          independent guidance; you always choose your own vendors and materials.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-[#0B1B2B] to-[#0e2c4d] text-white p-9 sm:p-12 text-center">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-extrabold">See where your 20% is hiding.</h2>
          <p className="mt-3 text-white/70 max-w-2xl mx-auto">Create your project in two minutes and let NCIS™ show you the cost heads, risks and savings specific to your build.</p>
          <Button onClick={createProject} data-testid="guide-cta"
            className="mt-7 bg-[#0E9AC8] hover:bg-white hover:text-slate-900 text-white rounded-xl h-14 px-8 text-base font-semibold transition-colors">
            Create My Project <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
